<script setup>
import { ref, computed, watch, onMounted } from "vue";
import {
  getTPUs,
  crearTPU,
  actualizarTPU,
  eliminarTPU,
  subirFichaTecnicaTPU,
  eliminarFichaTecnicaTPU,
  subirHojaSeguridadTPU,
  eliminarHojaSeguridadTPU,
} from "@/utils/api_backend";
import { Modal } from "bootstrap";
import Swal from "sweetalert2";
import { usePaginacionTPUsStore } from "@/store/paginacionStore";
import CrudToolbar from "@/components/Shared/CrudToolbar.vue";
import ColumnFilterMenu from "@/components/Shared/ColumnFilterMenu.vue";

const paginacionStore = usePaginacionTPUsStore();

const registros = ref([]);
const filtro = ref("");
const loading = ref(false);
const guardando = ref(false);
const editando = ref(false);
const paginaActual = ref(1);
const modalElement = ref(null);
let modalInstance = null;

const form = ref({ id: null, nombre: "", descripcion: "" });

// ── Ordenamiento ───────────────────────────────────────────
const sortKey = ref("nombre");
const sortDir = ref("asc");

function onSort({ field, asc }) {
  sortKey.value = field;
  sortDir.value = asc ? "asc" : "desc";
  paginaActual.value = 1;
}

watch(filtro, () => {
  paginaActual.value = 1;
});

// ── Computed ───────────────────────────────────────────────
const filtrados = computed(() => {
  const s = filtro.value.toLowerCase();
  return registros.value.filter(
    (r) =>
      r.nombre.toLowerCase().includes(s) ||
      (r.descripcion || "").toLowerCase().includes(s)
  );
});

const ordenados = computed(() =>
  [...filtrados.value].sort((a, b) => {
    const cmp = String(a[sortKey.value] ?? "").localeCompare(
      String(b[sortKey.value] ?? ""),
      "es",
      { sensitivity: "base" }
    );
    return sortDir.value === "asc" ? cmp : -cmp;
  })
);

const porPagina = computed(() => paginacionStore.porPagina);
const totalPaginas = computed(() =>
  Math.max(1, Math.ceil(ordenados.value.length / porPagina.value))
);
const paginados = computed(() => {
  const inicio = (paginaActual.value - 1) * porPagina.value;
  return ordenados.value.slice(inicio, inicio + porPagina.value);
});
const rangoInicio = computed(() =>
  ordenados.value.length === 0 ? 0 : (paginaActual.value - 1) * porPagina.value + 1
);
const rangoFin = computed(() =>
  Math.min(paginaActual.value * porPagina.value, ordenados.value.length)
);
const paginasVisibles = computed(() => {
  const total = totalPaginas.value;
  const actual = paginaActual.value;
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set([1, total, actual]);
  if (actual > 1) set.add(actual - 1);
  if (actual < total) set.add(actual + 1);
  const ordenadas = [...set].sort((a, b) => a - b);
  const resultado = [];
  for (let i = 0; i < ordenadas.length; i++) {
    if (i > 0 && ordenadas[i] - ordenadas[i - 1] > 1) resultado.push("...");
    resultado.push(ordenadas[i]);
  }
  return resultado;
});

function cambiarPorPagina(n) {
  paginacionStore.setPorPagina(n);
  paginaActual.value = 1;
}

// ── CRUD ───────────────────────────────────────────────────
async function cargar() {
  loading.value = true;
  try {
    registros.value = await getTPUs();
  } catch {
    Swal.fire("Error", "No se pudieron cargar los TPUs", "error");
  } finally {
    loading.value = false;
  }
}

function abrirModal(item = null) {
  if (item) {
    editando.value = true;
    form.value = {
      id: item.id,
      nombre: item.nombre,
      descripcion: item.descripcion || "",
    };
  } else {
    editando.value = false;
    form.value = { id: null, nombre: "", descripcion: "" };
  }
  modalInstance.show();
}

async function guardar() {
  guardando.value = true;
  try {
    const payload = {
      nombre: form.value.nombre,
      descripcion: form.value.descripcion || null,
    };
    if (editando.value) {
      await actualizarTPU(form.value.id, payload);
    } else {
      await crearTPU(payload);
    }
    await cargar();
    modalInstance.hide();
    Swal.fire({
      icon: "success",
      title: "Operación exitosa",
      text: `TPU ${editando.value ? "actualizado" : "registrado"} correctamente`,
      timer: 2000,
      showConfirmButton: false,
    });
  } catch {
    Swal.fire("Error", "No se pudo guardar el TPU", "error");
  } finally {
    guardando.value = false;
  }
}

async function confirmarBorrado(item) {
  const res = await Swal.fire({
    title: `¿Desactivar "${item.nombre}"?`,
    text: "El TPU será marcado como inactivo.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Sí, desactivar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "var(--d-brown)",
    cancelButtonColor: "var(--d-green)",
  });
  if (res.isConfirmed) {
    try {
      await eliminarTPU(item.id);
      await cargar();
      Swal.fire({
        icon: "success",
        title: "Listo",
        text: "TPU desactivado",
        timer: 1800,
        showConfirmButton: false,
      });
    } catch {
      Swal.fire("Error", "No se pudo desactivar el TPU", "error");
    }
  }
}

// ── Documentos TPU ─────────────────────────────────────────
// Refs para el modal de documentos
const modalDocElement = ref(null);
let modalDocInstance = null;
const tpuDoc = ref(null); // TPU activo en modal de documentos
const subiendoFicha = ref(false);
const subiendoHoja = ref(false);
const fileRefFicha = ref(null);
const fileRefHoja = ref(null);

// Retorna el documento más reciente de un array (último subido)
function ultimoDoc(arr) {
  if (!arr || arr.length === 0) return null;
  return arr[arr.length - 1];
}

function abrirModalDoc(item) {
  tpuDoc.value = { ...item };
  modalDocInstance.show();
}

async function handleSubirFicha(e) {
  const file = e.target.files[0];
  if (!file) return;
  subiendoFicha.value = true;
  try {
    const fd = new FormData();
    fd.append("documento", file);
    const nuevo = await subirFichaTecnicaTPU(tpuDoc.value.id, fd);
    // Actualizar local sin refetch
    const idx = registros.value.findIndex((r) => r.id === tpuDoc.value.id);
    if (idx !== -1) {
      registros.value[idx].fichas_tecnicas = [
        ...(registros.value[idx].fichas_tecnicas || []),
        nuevo,
      ];
      tpuDoc.value = { ...registros.value[idx] };
    }
    Swal.fire({
      icon: "success",
      title: "Ficha técnica subida",
      timer: 1800,
      showConfirmButton: false,
    });
  } catch {
    Swal.fire("Error", "No se pudo subir la ficha técnica", "error");
  } finally {
    subiendoFicha.value = false;
    if (fileRefFicha.value) fileRefFicha.value.value = "";
  }
}

async function handleSubirHoja(e) {
  const file = e.target.files[0];
  if (!file) return;
  subiendoHoja.value = true;
  try {
    const fd = new FormData();
    fd.append("documento", file);
    const nuevo = await subirHojaSeguridadTPU(tpuDoc.value.id, fd);
    const idx = registros.value.findIndex((r) => r.id === tpuDoc.value.id);
    if (idx !== -1) {
      registros.value[idx].hojas_seguridad = [
        ...(registros.value[idx].hojas_seguridad || []),
        nuevo,
      ];
      tpuDoc.value = { ...registros.value[idx] };
    }
    Swal.fire({
      icon: "success",
      title: "Hoja de seguridad subida",
      timer: 1800,
      showConfirmButton: false,
    });
  } catch {
    Swal.fire("Error", "No se pudo subir la hoja de seguridad", "error");
  } finally {
    subiendoHoja.value = false;
    if (fileRefHoja.value) fileRefHoja.value.value = "";
  }
}

async function confirmarEliminarDoc(tipo, doc) {
  const res = await Swal.fire({
    title: `¿Eliminar este documento?`,
    text: "Esta acción no se puede deshacer.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Sí, eliminar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "var(--d-brown)",
    cancelButtonColor: "var(--d-green)",
  });
  if (!res.isConfirmed) return;
  try {
    if (tipo === "ficha") {
      await eliminarFichaTecnicaTPU(doc.id);
      const idx = registros.value.findIndex((r) => r.id === tpuDoc.value.id);
      if (idx !== -1) {
        registros.value[idx].fichas_tecnicas = registros.value[
          idx
        ].fichas_tecnicas.filter((f) => f.id !== doc.id);
        tpuDoc.value = { ...registros.value[idx] };
      }
    } else {
      await eliminarHojaSeguridadTPU(doc.id);
      const idx = registros.value.findIndex((r) => r.id === tpuDoc.value.id);
      if (idx !== -1) {
        registros.value[idx].hojas_seguridad = registros.value[
          idx
        ].hojas_seguridad.filter((h) => h.id !== doc.id);
        tpuDoc.value = { ...registros.value[idx] };
      }
    }
    Swal.fire({
      icon: "success",
      title: "Documento eliminado",
      timer: 1500,
      showConfirmButton: false,
    });
  } catch {
    Swal.fire("Error", "No se pudo eliminar el documento", "error");
  }
}

onMounted(() => {
  cargar();
  modalInstance = new Modal(modalElement.value);
  modalDocInstance = new Modal(modalDocElement.value);
});
</script>

<template>
  <div class="crud-container" data-aos="fade-right">
    <CrudToolbar
      title="TPUs"
      v-model="filtro"
      searchPlaceholder="Buscar por nombre o descripción..."
    >
      <template #actions>
        <button class="btn btn-nuevo btn-header" @click="abrirModal(null)">
          <i class="bi bi-plus-circle-fill"></i>
          <span class="btn-header-texto ms-2">Nuevo TPU</span>
        </button>
      </template>
    </CrudToolbar>

    <!-- TABLA ESCRITORIO -->
    <div class="divisan-table-container shadow-lg d-none d-lg-block">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-verde"></div>
      </div>
      <table v-else class="table table-divisan align-middle mb-0">
        <thead>
          <tr>
            <th class="ps-4">
              <ColumnFilterMenu
                field="nombre"
                label="Nombre"
                :filterable="false"
                :items="filtrados"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                @update:sort="onSort"
              />
            </th>
            <th>
              <ColumnFilterMenu
                field="descripcion"
                label="Descripción"
                :filterable="false"
                :items="filtrados"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                @update:sort="onSort"
              />
            </th>
            <th class="text-center col-doc">Ficha técnica</th>
            <th class="text-center col-doc">Hoja de seguridad</th>
            <th class="text-center">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in paginados" :key="item.id">
            <td class="ps-4 fw-bold text-cafe">{{ item.nombre }}</td>
            <td>{{ item.descripcion || "—" }}</td>

            <!-- Ficha técnica -->
            <td class="text-center">
              <a
                v-if="ultimoDoc(item.fichas_tecnicas)"
                :href="ultimoDoc(item.fichas_tecnicas).documento"
                target="_blank"
                class="btn-doc"
                title="Ver ficha técnica"
              >
                <i class="bi bi-file-earmark-pdf-fill me-1"></i>Ver PDF
              </a>
              <span v-else class="text-cafe opacity-40 small">—</span>
            </td>

            <!-- Hoja de seguridad -->
            <td class="text-center">
              <a
                v-if="ultimoDoc(item.hojas_seguridad)"
                :href="ultimoDoc(item.hojas_seguridad).documento"
                target="_blank"
                class="btn-doc btn-doc-hoja"
                title="Ver hoja de seguridad"
              >
                <i class="bi bi-file-earmark-medical-fill me-1"></i>Ver PDF
              </a>
              <span v-else class="text-cafe opacity-40 small">—</span>
            </td>

            <td class="text-center">
              <div class="d-flex justify-content-center gap-2">
                <button
                  class="btn-circular"
                  @click="abrirModalDoc(item)"
                  title="Documentos"
                >
                  <i class="bi bi-folder2-open"></i>
                </button>
                <button class="btn-circular" @click="abrirModal(item)" title="Editar">
                  <i class="bi bi-pencil-square"></i>
                </button>
                <button
                  class="btn-circular text-danger"
                  @click="confirmarBorrado(item)"
                  title="Desactivar"
                >
                  <i class="bi bi-trash3-fill"></i>
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="paginados.length === 0 && !loading">
            <td colspan="5" class="text-center py-5 text-cafe opacity-50 fw-bold">
              No se encontraron TPUs
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Paginación escritorio -->
      <div class="paginacion-wrapper">
        <div class="pag-izq">
          <small class="pag-info"
            >{{ rangoInicio }}–{{ rangoFin }} de {{ ordenados.length }} TPUs</small
          >
          <div class="por-pagina-wrap">
            <label class="por-pagina-label">Filas:</label>
            <select
              class="por-pagina-select"
              :value="porPagina"
              @change="cambiarPorPagina(Number($event.target.value))"
            >
              <option v-for="n in paginacionStore.opcionesPorPagina" :key="n" :value="n">
                {{ n }}
              </option>
            </select>
          </div>
        </div>
        <div class="pag-der">
          <button
            class="btn-pag"
            :disabled="paginaActual === 1"
            @click="paginaActual = 1"
            title="Primera"
          >
            <i class="bi bi-chevron-double-left"></i>
          </button>
          <button class="btn-pag" :disabled="paginaActual === 1" @click="paginaActual--">
            <i class="bi bi-chevron-left"></i>
          </button>
          <template v-for="p in paginasVisibles" :key="p">
            <span v-if="p === '...'" class="pag-ellipsis">…</span>
            <button
              v-else
              :class="['btn-pag-num', { activo: p === paginaActual }]"
              @click="paginaActual = p"
            >
              {{ p }}
            </button>
          </template>
          <button
            class="btn-pag"
            :disabled="paginaActual === totalPaginas"
            @click="paginaActual++"
          >
            <i class="bi bi-chevron-right"></i>
          </button>
          <button
            class="btn-pag"
            :disabled="paginaActual === totalPaginas"
            @click="paginaActual = totalPaginas"
            title="Última"
          >
            <i class="bi bi-chevron-double-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- CARDS MÓVIL -->
    <div class="d-lg-none">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-verde"></div>
      </div>
      <div
        v-else
        v-for="item in paginados"
        :key="'mob-' + item.id"
        class="divisan-mobile-card mb-3 p-3 shadow-sm"
      >
        <div class="fw-bold text-cafe card-name mb-1">{{ item.nombre }}</div>
        <div class="small text-muted-cafe mb-2">
          {{ item.descripcion || "Sin descripción" }}
        </div>
        <!-- Documentos en móvil -->
        <div class="d-flex gap-2 mb-3 flex-wrap">
          <a
            v-if="ultimoDoc(item.fichas_tecnicas)"
            :href="ultimoDoc(item.fichas_tecnicas).documento"
            target="_blank"
            class="btn-doc"
          >
            <i class="bi bi-file-earmark-pdf-fill me-1"></i>Ficha técnica
          </a>
          <a
            v-if="ultimoDoc(item.hojas_seguridad)"
            :href="ultimoDoc(item.hojas_seguridad).documento"
            target="_blank"
            class="btn-doc btn-doc-hoja"
          >
            <i class="bi bi-file-earmark-medical-fill me-1"></i>Hoja de seguridad
          </a>
        </div>
        <div class="d-flex justify-content-end gap-3 pt-2 border-top">
          <i
            class="bi bi-folder2-open text-verde fs-5"
            style="cursor: pointer"
            @click="abrirModalDoc(item)"
            title="Gestionar documentos"
          ></i>
          <i
            class="bi bi-pencil-square text-verde fs-5"
            style="cursor: pointer"
            @click="abrirModal(item)"
          ></i>
          <i
            class="bi bi-trash3-fill text-danger fs-5"
            style="cursor: pointer"
            @click="confirmarBorrado(item)"
          ></i>
        </div>
      </div>
      <div class="paginacion-mobile">
        <div class="pag-mobile-top">
          <small class="pag-info"
            >{{ rangoInicio }}–{{ rangoFin }} de {{ ordenados.length }} TPUs</small
          >
          <div class="por-pagina-wrap">
            <label class="por-pagina-label">Filas:</label>
            <select
              class="por-pagina-select"
              :value="porPagina"
              @change="cambiarPorPagina(Number($event.target.value))"
            >
              <option v-for="n in paginacionStore.opcionesPorPagina" :key="n" :value="n">
                {{ n }}
              </option>
            </select>
          </div>
        </div>
        <div class="pag-mobile-nav">
          <button
            class="btn-pag-mobile"
            :disabled="paginaActual === 1"
            @click="paginaActual--"
          >
            <i class="bi bi-chevron-left"></i> Anterior
          </button>
          <small class="text-verde fw-bold"
            >{{ paginaActual }} / {{ totalPaginas }}</small
          >
          <button
            class="btn-pag-mobile"
            :disabled="paginaActual === totalPaginas"
            @click="paginaActual++"
          >
            Siguiente <i class="bi bi-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- MODAL -->
    <div class="modal fade" tabindex="-1" aria-hidden="true" ref="modalElement">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content divisan-modal border-0">
          <div class="modal-header border-0 pb-0">
            <h5 class="modal-title fw-bold text-cafe">
              {{ editando ? "Editar TPU" : "Nuevo TPU" }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <form @submit.prevent="guardar">
            <div class="modal-body">
              <div class="mb-3">
                <label class="form-label label-divisan"
                  >Nombre <span class="text-danger">*</span></label
                >
                <input
                  v-model="form.nombre"
                  type="text"
                  class="form-control divisan-input-modal"
                  required
                  placeholder="Ej. TPU-90A"
                />
              </div>
              <div class="mb-3">
                <label class="form-label label-divisan"
                  >Descripción <span class="label-opcional">(opcional)</span></label
                >
                <textarea
                  v-model="form.descripcion"
                  class="form-control divisan-input-modal"
                  rows="3"
                  placeholder="Descripción del TPU..."
                ></textarea>
              </div>
            </div>
            <div class="modal-footer border-0 pt-0">
              <button
                type="submit"
                class="btn btn-nuevo w-100 py-2"
                :disabled="guardando"
              >
                <span
                  v-if="guardando"
                  class="spinner-border spinner-border-sm me-2"
                ></span>
                {{ editando ? "Actualizar TPU" : "Guardar TPU" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- MODAL DOCUMENTOS -->
    <div class="modal fade" tabindex="-1" aria-hidden="true" ref="modalDocElement">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content divisan-modal border-0">
          <div class="modal-header border-0 pb-0">
            <div>
              <h5 class="modal-title fw-bold text-cafe mb-0">Documentos del TPU</h5>
              <small class="text-cafe opacity-60 fw-bold">{{ tpuDoc?.nombre }}</small>
            </div>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <div class="row g-4" v-if="tpuDoc">
              <!-- ── Ficha técnica ── -->
              <div class="col-12 col-md-6">
                <div class="doc-seccion">
                  <div class="doc-seccion-header">
                    <i class="bi bi-file-earmark-pdf-fill text-danger me-2"></i>
                    <span class="fw-bold text-cafe">Fichas técnicas</span>
                    <span class="badge-conteo ms-2">{{
                      (tpuDoc.fichas_tecnicas || []).length
                    }}</span>
                  </div>

                  <!-- Lista de fichas existentes -->
                  <div class="doc-lista mt-2">
                    <div
                      v-for="doc in tpuDoc.fichas_tecnicas || []"
                      :key="'ft-' + doc.id"
                      class="doc-item"
                    >
                      <a :href="doc.documento" target="_blank" class="doc-link">
                        <i class="bi bi-file-earmark-pdf me-1"></i>
                        {{
                          doc.fecha_subida
                            ? new Date(doc.fecha_subida).toLocaleDateString("es-MX")
                            : "Sin fecha"
                        }}
                      </a>
                      <button
                        class="btn-doc-eliminar"
                        @click="confirmarEliminarDoc('ficha', doc)"
                        title="Eliminar"
                      >
                        <i class="bi bi-trash3-fill"></i>
                      </button>
                    </div>
                    <p
                      v-if="!(tpuDoc.fichas_tecnicas || []).length"
                      class="text-cafe opacity-40 small mb-0"
                    >
                      Sin fichas registradas
                    </p>
                  </div>

                  <!-- Subir nueva -->
                  <div class="mt-3">
                    <label class="form-label label-divisan small"
                      >Subir nueva ficha</label
                    >
                    <input
                      ref="fileRefFicha"
                      type="file"
                      accept="application/pdf"
                      class="form-control divisan-input-modal"
                      :disabled="subiendoFicha"
                      @change="handleSubirFicha"
                    />
                    <div
                      v-if="subiendoFicha"
                      class="d-flex align-items-center gap-2 mt-2"
                    >
                      <div class="spinner-border spinner-border-sm text-verde"></div>
                      <small class="text-cafe">Subiendo...</small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- ── Hoja de seguridad ── -->
              <div class="col-12 col-md-6">
                <div class="doc-seccion">
                  <div class="doc-seccion-header">
                    <i class="bi bi-file-earmark-medical-fill text-verde me-2"></i>
                    <span class="fw-bold text-cafe">Hojas de seguridad</span>
                    <span class="badge-conteo ms-2">{{
                      (tpuDoc.hojas_seguridad || []).length
                    }}</span>
                  </div>

                  <div class="doc-lista mt-2">
                    <div
                      v-for="doc in tpuDoc.hojas_seguridad || []"
                      :key="'hs-' + doc.id"
                      class="doc-item"
                    >
                      <a :href="doc.documento" target="_blank" class="doc-link">
                        <i class="bi bi-file-earmark-medical me-1"></i>
                        {{
                          doc.fecha_subida
                            ? new Date(doc.fecha_subida).toLocaleDateString("es-MX")
                            : "Sin fecha"
                        }}
                      </a>
                      <button
                        class="btn-doc-eliminar"
                        @click="confirmarEliminarDoc('hoja', doc)"
                        title="Eliminar"
                      >
                        <i class="bi bi-trash3-fill"></i>
                      </button>
                    </div>
                    <p
                      v-if="!(tpuDoc.hojas_seguridad || []).length"
                      class="text-cafe opacity-40 small mb-0"
                    >
                      Sin hojas registradas
                    </p>
                  </div>

                  <div class="mt-3">
                    <label class="form-label label-divisan small">Subir nueva hoja</label>
                    <input
                      ref="fileRefHoja"
                      type="file"
                      accept="application/pdf"
                      class="form-control divisan-input-modal"
                      :disabled="subiendoHoja"
                      @change="handleSubirHoja"
                    />
                    <div v-if="subiendoHoja" class="d-flex align-items-center gap-2 mt-2">
                      <div class="spinner-border spinner-border-sm text-verde"></div>
                      <small class="text-cafe">Subiendo...</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer border-0 pt-0">
            <button type="button" class="btn btn-nuevo px-4" data-bs-dismiss="modal">
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.text-cafe {
  color: var(--d-brown);
}
.text-verde {
  color: var(--d-green);
}
.text-muted-cafe {
  color: var(--d-brown);
  opacity: 0.6;
  font-size: 0.9rem;
}

.btn-nuevo {
  background-color: var(--d-brown);
  color: var(--d-cream);
  border: none;
  font-weight: 600;
}
.btn-nuevo:hover:not(:disabled) {
  background-color: #703808;
  color: var(--d-cream);
}
.btn-header {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

.divisan-card {
  background-color: var(--d-cream);
  border-radius: 12px;
  border-left: 5px solid var(--d-green);
}
.divisan-input {
  background-color: white !important;
  border: 1px solid var(--d-green);
  color: var(--d-brown) !important;
  border-radius: 8px;
}
.search-wrapper {
  position: relative;
}
.search-wrapper i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--d-green);
  z-index: 5;
  pointer-events: none;
}
.search-wrapper input {
  padding-left: 35px;
}

.divisan-table-container {
  background-color: var(--d-cream);
  border-radius: var(--d-radius-lg);
  box-shadow: var(--d-elevation-2);
  overflow: hidden;
}
.table-divisan thead th {
  background: var(--d-cream);
  color: var(--d-brown);
  border-bottom: 1px solid var(--d-border);
}
.table-divisan th {
  padding: 15px;
  font-weight: 800;
  text-transform: capitalize;
  border: none;
  white-space: nowrap;
}
.table-divisan td {
  border-bottom: 1px solid rgba(96, 108, 56, 0.1);
  padding: 12px 15px;
}
.table-divisan tbody tr:hover td {
  background-color: rgba(96, 108, 56, 0.05);
}

.btn-circular {
  background: white;
  border: 1px solid var(--d-green);
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--d-green);
  transition: 0.2s;
}
.btn-circular:hover {
  background-color: var(--d-green);
  color: var(--d-cream);
}

.divisan-modal {
  background-color: var(--d-cream);
  border: 2px solid var(--d-brown);
  border-radius: 20px;
}

@media (max-width: 1000px) {
  .divisan-modal {
    margin-top: 4rem;
  }
}

.label-divisan {
  color: var(--d-brown);
  font-weight: 700;
}
.label-opcional {
  font-size: 0.72rem;
  font-weight: 400;
  color: var(--d-brown);
  opacity: 0.5;
}
.divisan-input-modal {
  border: 1px solid var(--d-green);
  border-radius: 8px;
}

.divisan-mobile-card {
  background-color: var(--d-cream);
  border-radius: 15px;
  border-left: 6px solid var(--d-green);
}

.paginacion-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  flex-wrap: wrap;
  gap: 8px;
  border-top: 1px solid rgba(64, 34, 8, 0.1);
  background: rgba(96, 108, 56, 0.05);
}
.pag-izq {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}
.pag-der {
  display: flex;
  align-items: center;
  gap: 4px;
}
.pag-info {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--d-green);
  white-space: nowrap;
}
.por-pagina-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}
.por-pagina-label {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--d-green);
  white-space: nowrap;
}
.por-pagina-select {
  appearance: none;
  -webkit-appearance: none;
  background: white
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%23606c38' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E")
    no-repeat right 8px center;
  border: 1.5px solid var(--d-green);
  border-radius: 8px;
  padding: 3px 26px 3px 10px;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--d-brown);
  cursor: pointer;
  outline: none;
  transition: border-color 0.15s;
  min-width: 58px;
}
.por-pagina-select:focus,
.por-pagina-select:hover {
  border-color: var(--d-brown);
}
.btn-pag {
  background: white;
  border: 1.5px solid rgba(96, 108, 56, 0.35);
  color: var(--d-brown);
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s;
  font-size: 0.8rem;
}
.btn-pag:hover:not(:disabled) {
  background: var(--d-green);
  color: var(--d-cream);
  border-color: var(--d-green);
}
.btn-pag:disabled {
  opacity: 0.3;
  cursor: default;
}
.btn-pag-num {
  background: white;
  border: 1.5px solid rgba(96, 108, 56, 0.25);
  color: var(--d-brown);
  border-radius: 8px;
  min-width: 32px;
  height: 32px;
  padding: 0 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s;
  font-size: 0.82rem;
  font-weight: 700;
}
.btn-pag-num:hover {
  background: rgba(96, 108, 56, 0.1);
  border-color: var(--d-green);
}
.btn-pag-num.activo {
  background: var(--d-brown);
  color: var(--d-cream);
  border-color: var(--d-brown);
  pointer-events: none;
}
.pag-ellipsis {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 32px;
  font-size: 0.85rem;
  color: var(--d-brown);
  opacity: 0.4;
  font-weight: 700;
}

.paginacion-mobile {
  border-top: 1px solid rgba(64, 34, 8, 0.15);
  padding: 10px 4px;
  margin-top: 4px;
}
.pag-mobile-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}
.pag-mobile-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.btn-pag-mobile {
  background: var(--d-cream);
  border: 1px solid var(--d-green);
  color: var(--d-brown);
  border-radius: 10px;
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 110px;
  justify-content: center;
}
.btn-pag-mobile:hover:not(:disabled) {
  background-color: var(--d-green);
  color: var(--d-cream);
}
.btn-pag-mobile:disabled {
  opacity: 0.35;
  cursor: default;
}

.card-name {
  font-size: 0.92rem;
}

@media (max-width: 991.98px) {
  .btn-header-texto {
    display: none;
  }
  .btn-header {
    width: 36px;
    height: 36px;
    padding: 0;
    justify-content: center;
    border-radius: 50%;
  }
}

@media (max-width: 768px) {
  .paginacion-wrapper {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
}

/* ── Columnas de documento ── */
.col-doc {
  width: 130px;
}

/* Botón Ver PDF en tabla — estilo cotización de refacciones */
.btn-doc {
  display: inline-flex;
  align-items: center;
  background: rgba(220, 53, 69, 0.08);
  color: #dc3545;
  border: 1px solid rgba(220, 53, 69, 0.25);
  border-radius: 6px;
  padding: 3px 10px;
  font-size: 0.78rem;
  font-weight: 700;
  text-decoration: none;
  transition: all 0.15s;
  white-space: nowrap;
}
.btn-doc:hover {
  background: #dc3545;
  color: white;
  border-color: #dc3545;
}
.btn-doc-hoja {
  background: rgba(96, 108, 56, 0.1);
  color: var(--d-green);
  border-color: rgba(96, 108, 56, 0.3);
}
.btn-doc-hoja:hover {
  background: var(--d-green);
  color: white;
  border-color: var(--d-green);
}

/* Modal de documentos */
.doc-seccion {
  background: white;
  border: 1.5px solid rgba(96, 108, 56, 0.2);
  border-radius: 12px;
  padding: 16px;
  height: 100%;
}
.doc-seccion-header {
  display: flex;
  align-items: center;
  padding-bottom: 10px;
  border-bottom: 2px solid rgba(64, 34, 8, 0.08);
}
.badge-conteo {
  background: rgba(96, 108, 56, 0.15);
  color: var(--d-green-dark);
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 0.72rem;
  font-weight: 800;
}
.doc-lista {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 40px;
}
.doc-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(96, 108, 56, 0.05);
  border-radius: 6px;
  padding: 5px 10px;
}
.doc-link {
  color: var(--d-brown);
  font-size: 0.82rem;
  font-weight: 600;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  flex: 1;
}
.doc-link:hover {
  color: var(--d-green);
  text-decoration: underline;
}
.btn-doc-eliminar {
  background: none;
  border: none;
  color: rgba(220, 53, 69, 0.5);
  padding: 2px 6px;
  cursor: pointer;
  transition: color 0.15s;
  font-size: 0.8rem;
  flex-shrink: 0;
}
.btn-doc-eliminar:hover {
  color: #dc3545;
}
</style>
